import { Icon } from '@iconify/react'
import { Dropdown, Form, Space, Tooltip } from 'antd'
import {
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { AuthContext } from 'src/components/Auth/AuthProvider'
import { CopyButton } from 'src/components/buttons'
import { TextInput } from 'src/components/inputs/'
import { getSummaryCanvas } from 'src/utils/platform'
import FeedFwdCanvasHeader from './FeedFwdCanvasHeader'
import FeedFwdModal from './FeedFwdModal'
import STTButton from 'src/components/common/STTButton'
import { useSpeechRecognition } from 'react-speech-recognition'
import { CommunicationContext } from 'src/context/CommunicationContext'
import { CAI_CHAT_FLOWS } from 'src/constants/cai-chat.constant'

type CaiChatFlowType = (typeof CAI_CHAT_FLOWS)['GOALS' | 'FEEDBACK']

type FormatType = 'direct' | 'structured'

interface FeedFwdCanvasProps {
  handleMouseDownFirst: (e: React.MouseEvent<HTMLDivElement>) => void
  awaitingResponse: boolean
  flow?: string
  currentFeedback: string
  setFlow: Dispatch<SetStateAction<string>>
  setCurrentFeedback: Dispatch<SetStateAction<Record<CaiChatFlowType, string>>>
}

const FeedFwdCanvas: FC<FeedFwdCanvasProps> = (props) => {
  const {
    awaitingResponse,
    flow,
    currentFeedback,
    setCurrentFeedback,
    setFlow,
  } = props
  const { t } = useTranslation()
  const { auth } = useContext(AuthContext)
  const [loading, setLoading] = useState(false)
  const [form] = Form.useForm()
  const { canvasRef, wankRef } = useContext(CommunicationContext)

  const handleSetCurrentFeedback = (value: string) => {
    setCurrentFeedback((prevState) => ({
      ...prevState,
      feedback: value,
    }))
  }

  const handleSummaryCanvas = async (formatType: FormatType) => {
    setLoading(true)
    const newText = await getSummaryCanvas(
      auth?.token,
      currentFeedback,
      formatType,
    )
    handleSetCurrentFeedback(newText)
    setLoading(false)
  }

  const items = [
    {
      key: '1',
      label: (
        <div
          className="d-flex  align-items-center"
          onClick={() => handleSummaryCanvas('structured')}
        >
          <Icon
            height="20"
            icon="material-symbols-light:document-scanner"
            width="20"
          />
          <div className="ms-1">{t('feedbacks.items.formatStructure')}</div>
        </div>
      ),
    },
    {
      key: '2',
      label: (
        <div
          className="d-flex  align-items-center"
          onClick={() => handleSummaryCanvas('direct')}
        >
          <Icon height="20" icon="material-symbols-light:pending" width="20" />
          <div className="ms-1">{t('feedbacks.items.rewrite')}</div>
        </div>
      ),
    },
  ]

  const { listening, ...speechApi } = useSpeechRecognition()

  return (
    <div
      ref={canvasRef}
      className="d-flex w-100 h-100 flex-column position-relative p-1"
    >
      <FeedFwdCanvasHeader
        currentFeedback={currentFeedback}
        flow={flow}
        form={form}
      />
      <div
        style={{
          width: '100%',
          height: '100%',
          background: 'white',
          marginBottom: '5px',
          position: 'relative',
          paddingBottom: '40px',
          pointerEvents: loading ? 'none' : undefined,

          color: awaitingResponse || loading ? 'rgba(0,0,0,0.25)' : 'black',
        }}
      >
        <TextInput
          autoSize={false}
          disabled={loading || awaitingResponse}
          value={currentFeedback}
          placeholder={t(
            'feedbacks.actionChatMsg',
            'You can type directly here...',
          )}
          style={{
            height: '100%',
            resize: 'none',
          }}
          sx={{
            padding: 0,
            width: '100%',
            height: '100%',
            borderRadius: 0,
            border: 0,
            background: 'white',
          }}
          sxBox={{
            height: '100%',
            display: 'block',
            maxHeight: '100%',
          }}
          onChange={(e) => {
            handleSetCurrentFeedback(e.target.value)
          }}
        />

        <div
          className="d-flex justify-content-start align-items-center w-100"
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            padding: '5px',
            pointerEvents: awaitingResponse || loading ? 'none' : undefined,
            opacity: awaitingResponse || loading ? 0.5 : 1,
          }}
        >
          <Space>
            <STTButton
              setText={handleSetCurrentFeedback}
              {...speechApi}
              awaitingResponse={awaitingResponse}
              height="24"
              width="24"
            />
            <Dropdown
              disabled={awaitingResponse}
              menu={{ items }}
              placement="topLeft"
            >
              <Icon
                ref={wankRef as unknown as React.RefObject<SVGSVGElement>}
                color="var(--color-primary)"
                height="24"
                icon="ph:magic-wand-fill"
                type="button"
                width="24"
              />
            </Dropdown>
          </Space>

          <div className="w-100 d-flex justify-content-end align-items-center gap-1">
            <CopyButton
              text={currentFeedback}
              style={{
                background: 'var(--color-primary)',
                color: 'white',
                borderRadius: '50%',
                padding: '8px',
              }}
            />
          </div>
          <FeedFwdModal
            currentFeedback={currentFeedback}
            flow={flow}
            form={form}
            setCurrentFeedback={handleSetCurrentFeedback}
            setFlow={setFlow}
          />
        </div>
      </div>
    </div>
  )
}

export default FeedFwdCanvas
